import config from '@/config';
import { UserState } from '@/types/user';
import common from '@/utils/common';

export type GuideType = 'email' | 'class-code' | 'google-sign-in' | 'google-classroom';

export type UserRole = 'teacher' | 'student' | 'admin';

export interface PdfConfig {
  type: GuideType;
  role: UserRole;
  language: string;
}

export interface PdfInfo {
  type: GuideType;
  role: UserRole;
  url: string;
  title: string;
}

class PdfService {
  private readonly supportedLanguages = ['en', 'es', 'pt'];
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = this.normalizeBaseUrl(config.s3BaseUrl || config.apiUrl || '');
  }

  getGuideTypeForUser(user: UserState | null, schoolClassCodeEnabled?: boolean): GuideType {
    if (common.isMGWebsite()) {
      return 'google-sign-in';
    }

    if (common.isGoogle(user)) {
      return 'google-classroom';
    }

    if (common.isClassCode(user, schoolClassCodeEnabled)) {
      return 'class-code';
    }

    return 'email';
  }

  getAppropriatePdfs(
    user: UserState | null,
    language: string,
    schoolClassCodeEnabled?: boolean
  ): PdfInfo[] {
    const guideType = this.getGuideTypeForUser(user, schoolClassCodeEnabled);

    const pdfs: PdfInfo[] = [];

    if (user?.metadata?.role === 'admin') {
      pdfs.push(this.getPdfInfo(guideType, 'admin', language));
    }

    pdfs.push(
      this.getPdfInfo(guideType, 'teacher', language),
      this.getPdfInfo(guideType, 'student', language)
    );

    return pdfs;
  }

  getAppropriatePdf(
    user: UserState | null,
    language: string,
    schoolClassCodeEnabled?: boolean
  ): PdfInfo {
    const guideType = this.getGuideTypeForUser(user, schoolClassCodeEnabled);

    return this.getPdfInfo(guideType, 'teacher', language);
  }

  getAvailablePdfs(userLanguage: string, role: UserRole): PdfInfo[] {
    const normalizedLanguage = this.normalizeLanguage(userLanguage);
    const guideTypes: GuideType[] = ['email', 'class-code', 'google-sign-in', 'google-classroom'];

    return guideTypes.map(type => ({
      type,
      role,
      url: this.getPdfUrl(type, role, normalizedLanguage),
      title: this.getFileName(role),
    }));
  }

  getSpecificPdfs(userLanguage: string, role: UserRole, types: GuideType[]): PdfInfo[] {
    const normalizedLanguage = this.normalizeLanguage(userLanguage);

    return types.map(type => ({
      type,
      role,
      url: this.getPdfUrl(type, role, normalizedLanguage),
      title: this.getFileName(role),
    }));
  }

  getPdfInfo(type: GuideType, role: UserRole, language: string): PdfInfo {
    const normalizedLanguage = this.normalizeLanguage(language);

    return {
      type,
      role,
      url: this.getPdfUrl(type, role, normalizedLanguage),
      title: this.getFileName(role),
    };
  }

  getGuideTypePdfs(type: GuideType, language: string): PdfInfo[] {
    const normalizedLanguage = this.normalizeLanguage(language);

    return ['teacher', 'student'].map(role => ({
      type,
      role: role as UserRole,
      url: this.getPdfUrl(type, role as UserRole, normalizedLanguage),
      title: this.getFileName(role as UserRole),
    }));
  }

  getPdfUrl(type: GuideType, role: UserRole, language: string): string {
    if (role === 'admin') {
      const normalizedLanguage = this.normalizeLanguage(language);
      return `${this.baseUrl}/instructions/${normalizedLanguage}/admin/admin_guide.pdf`;
    }
    const normalizedLanguage = this.normalizeLanguage(language);
    const fileName = this.getFileName(role);
    return `${this.baseUrl}/instructions/${normalizedLanguage}/${type}/${fileName}`;
  }

  private normalizeLanguage(language: string): string {
    return this.supportedLanguages.includes(language) ? language : 'en';
  }

  private getFileName(role: UserRole): string {
    return `${role}_guide.pdf`;
  }

  private normalizeBaseUrl(url: string): string {
    return url.replace(/\/+$/, '');
  }

  isLanguageSupported(language: string): boolean {
    return this.supportedLanguages.includes(language);
  }

  getSupportedLanguages(): string[] {
    return [...this.supportedLanguages];
  }

  getAvailableGuideTypes(): GuideType[] {
    return ['email', 'class-code', 'google-sign-in', 'google-classroom'];
  }
}

export default new PdfService();
